'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { getWebnotesMenuTree } from '@nx-frontend/shared/api';
import { getWorkspaceTags } from '@nx-frontend/shared/api/webnotes';
import { generalEnv } from '@nx-frontend/shared/env';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import {
  menuItemsToMenuState,
  resetMenuItemsEvent,
  setActiveItemIdEvent,
  initExpandedMenuItemsFx,
  setMenuWorkspaceIdEvent,
} from '@nx-frontend/shared/store/feature-menu-tree';
import { createTagsMenuItemDtos, getSidebarMenuTreeDefaultItems } from '@nx-frontend/shared/store/internal-space-menu';
import {
  expandAllParentFoldersEvent,
  expandAllPortalParentFoldersEvent,
  getSidebarMenuTree,
} from '@nx-frontend/shared/store/internal-space-menu';
import { $note } from '@nx-frontend/shared/store/note';
import { fetchWorkspaceFoldersEvent } from '@nx-frontend/shared/store/notes';
import {
  $portalSettings,
  fetchPortalExpandedItemsInSidebarFx,
  setPortalExpandedItemsInListEvent,
} from '@nx-frontend/shared/store/portal';
import { resetSidebarFetchMenuTreeEvent } from '@nx-frontend/shared/store/sidebar';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { UnleashFeatures } from '@nx-frontend/shared/types';
import { MenuItemType, MenuType } from '@nx-frontend/shared/types/menu';
import { NoteDto } from '@nx-frontend/shared/types/note';
import { generateTagId, positionTypeToMenuType } from '@nx-frontend/shared/utils';
import { scrollToSidebarMenuItem } from '@nx-frontend/shared/utils';

interface UseSelectCurrentItemProps {
  pageType: string;
  itemId: string;
  currentStoreNote: NoteDto | null;
  fetchExpandedItems(): Promise<unknown>;
  setWorkspaceId?: (workspaceId: string) => void;
  /**
   * If true, the sidebar will not scroll to the selected note.
   */
  doNotScrollToNote: boolean;
}

let foldersAbortController: AbortController | null = null;

/**
 * Watches if selected note changed and selects it in the tree.
 * Also fetches folders and pages for selected workspace when the workspace changes.
 * @param param0
 * @returns
 */
export function useSelectCurrentItem({
  pageType,
  itemId,
  currentStoreNote,
  fetchExpandedItems,
  setWorkspaceId,
  doNotScrollToNote,
}: UseSelectCurrentItemProps) {
  const currentItemIdRef = useRef<string | null>(null);
  const [currentWorkspace, portalSettings, note] = useUnit([$currentWorkspace, $portalSettings, $note]);
  const [prevWorkspaceId, setPrevWorkspaceId] = useState<string | null>(null);
  const [currentItemId, setCurrentItemId] = useState<string | null>(null);
  const [currentNoteParentId, setCurrentNoteParentId] = useState<string | null>(null);
  const [
    expandAllParentFolders,
    setActiveItemId,
    setPortalExpandedItemsInList,
    fetchPortalExpandedItemsInSidebar,
    expandAllPortalParentFolders,
    resetMenuItems,
    initExpandedMenuItems,
    resetSidebarFetchMenuTree,
    fetchWorkspaceFolders,
    setMenuWorkspaceId,
    unleashFlags,
  ] = useUnit([
    expandAllParentFoldersEvent,
    setActiveItemIdEvent,
    setPortalExpandedItemsInListEvent,
    fetchPortalExpandedItemsInSidebarFx,
    expandAllPortalParentFoldersEvent,
    resetMenuItemsEvent,
    initExpandedMenuItemsFx,
    resetSidebarFetchMenuTreeEvent,
    fetchWorkspaceFoldersEvent,
    setMenuWorkspaceIdEvent,
    $unleashFlags,
  ]);
  const t = useTranslations();

  useEffect(() => {
    setCurrentNoteParentId(note.parentId);
  }, [note.parentId]);

  useEffect(() => {
    if (currentWorkspace.globalId !== prevWorkspaceId) {
      setPrevWorkspaceId(currentWorkspace.globalId);
    }
  }, [currentWorkspace.globalId, prevWorkspaceId]);

  // fetch folders and pages for selected workspace
  const fetchWorkspaceMenuTree = useCallback(
    (workspaceId: string) => {
      resetMenuItems({
        menuType: MenuType.WEBNOTES_MENU,
        menuState: menuItemsToMenuState(
          getSidebarMenuTreeDefaultItems({
            disableTags: !unleashFlags[UnleashFeatures.NOTE_TAGS],
            disableAIAgents: !unleashFlags[UnleashFeatures.AI_AGENTS],
          })
        ),
      });

      initExpandedMenuItems();
      if (foldersAbortController) {
        foldersAbortController.abort();
      }
      foldersAbortController = new AbortController();
      getWebnotesMenuTree({
        gatewayUrl: generalEnv.gatewayUrl,
        workspaceId,
        menuType: MenuType.WEBNOTES_MENU,
        type: 'folder',
        depth: -1,
        abortController: foldersAbortController,
        orderBy: 'title',
        orderDirection: 'ASC',
      }).then(async (folders) => {
        const tags = await getWorkspaceTags({ workspaceId });
        resetMenuItems({
          menuType: MenuType.WEBNOTES_MENU,
          menuState: menuItemsToMenuState(
            getSidebarMenuTree({
              fetchedItems: folders,
              tagsItems: unleashFlags[UnleashFeatures.NOTE_TAGS] ? createTagsMenuItemDtos(tags ?? []) : undefined,
              t,
              showAIAgents: unleashFlags[UnleashFeatures.AI_AGENTS],
            })
          ),
        });
        resetSidebarFetchMenuTree();
        await fetchExpandedItems();

        setCurrentItemId((activeItemId) => {
          if (activeItemId) {
            setCurrentNoteParentId((currentNoteParentId) => {
              const parentId: string | undefined = `${MenuItemType.NOTES_FOLDER}#${currentNoteParentId}`;
              expandAllParentFolders({
                menuType: MenuType.WEBNOTES_MENU,
                id: activeItemId,
                parentId,
                workspaceId,
              });
              setActiveItemId({ menuType: MenuType.WEBNOTES_MENU, id: activeItemId });
              if (!doNotScrollToNote) {
                setTimeout(() => {
                  scrollToSidebarMenuItem(activeItemId);
                }, 0);
              }
              return currentNoteParentId;
            });
          }
          return activeItemId;
        });
      });
    },
    [
      resetMenuItems,
      unleashFlags,
      initExpandedMenuItems,
      t,
      resetSidebarFetchMenuTree,
      fetchExpandedItems,
      expandAllParentFolders,
      setActiveItemId,
      doNotScrollToNote,
    ]
  );

  useEffect(() => {
    if (currentWorkspace.globalId !== prevWorkspaceId) {
      fetchWorkspaceMenuTree(currentWorkspace.globalId);
      if (setWorkspaceId) {
        setWorkspaceId(currentWorkspace.globalId);
      }

      // fetch all folders if are empty
      fetchWorkspaceFolders({
        workspaceId: currentWorkspace.globalId,
        type: 'folder',
        rootId: 'root',
        fetchPortalsNotes: false,
        counters: true,
        range: {},
      });

      // for fetching notes on expand a folder
      setMenuWorkspaceId(currentWorkspace.globalId);
    }
  });

  useEffect(() => {
    let id: string | null = null;
    if (pageType === 'page') {
      id = `${MenuItemType.NOTE}#${itemId}`;
    }
    if (pageType === 'folder') {
      id = `${MenuItemType.NOTES_FOLDER}#${itemId}`;
    }
    if (pageType === 'tag') {
      id = `${MenuItemType.TAG}#${generateTagId(itemId)}`;
    }
    if (pageType === 'all') {
      id = `${MenuItemType.ALL_PAGES}#${MenuItemType.ALL_PAGES}`;
    }
    if (pageType === 'files') {
      id = `${MenuItemType.FILES_MANAGER}#${MenuItemType.FILES_MANAGER}`;
    }
    if (pageType === 'tasks') {
      id = `${MenuItemType.TASKS}#${MenuItemType.TASKS}`;
    }
    if (pageType === 'chat') {
      id = `${MenuItemType.CHAT}#${MenuItemType.CHAT}`;
    }
    if (pageType === 'agents') {
      id = `${MenuItemType.AI_AGENTS}#${MenuItemType.AI_AGENTS}`;
    }

    setCurrentItemId(id);
  }, [itemId, pageType]);

  return useEffect(() => {
    const id = currentItemId;
    if (id) {
      // select portal menu item
      const { portalMenuItem, isSharedForPortal } = currentStoreNote || {};
      const isPortalNote = portalMenuItem && isSharedForPortal;

      if (currentItemIdRef.current !== id) {
        currentItemIdRef.current = id;

        if (!isPortalNote) {
          const parentId: string | undefined = `${MenuItemType.NOTES_FOLDER}#${currentNoteParentId}`;
          expandAllParentFolders({
            menuType: MenuType.WEBNOTES_MENU,
            id,
            parentId,
            workspaceId: currentWorkspace.globalId,
            doNotScrollToNote,
          });
          setActiveItemId({ menuType: MenuType.WEBNOTES_MENU, id });
          if (!doNotScrollToNote) {
            scrollToSidebarMenuItem(id);
          }
        } else if (
          portalMenuItem.positionType &&
          // we can't trust the portalMenuItem value because currentStoreNote loads asynchronously and might be for different note(previously loaded),
          // so we need to check if ids are the same
          currentStoreNote &&
          currentStoreNote.globalId === itemId
        ) {
          const menuType = positionTypeToMenuType(portalMenuItem.positionType);

          setPortalExpandedItemsInList({
            portalId: portalSettings.id,
            itemIds: ['portal', 'contentAndLinks', menuType],
          });
          fetchPortalExpandedItemsInSidebar();

          expandAllPortalParentFolders({
            menuType,
            id: portalMenuItem.id,
            parentId: portalMenuItem.parentId,
          });
          setActiveItemId({
            menuType,
            id: portalMenuItem.id,
          });
          setActiveItemId({ menuType: MenuType.WEBNOTES_MENU, id: null });
          if (!doNotScrollToNote) {
            scrollToSidebarMenuItem(portalMenuItem.id);
          }
        }
      }
    }
  }, [
    pageType,
    itemId,
    currentWorkspace.globalId,
    currentNoteParentId,
    currentStoreNote,
    setActiveItemId,
    resetMenuItems,
    fetchExpandedItems,
    setMenuWorkspaceId,
    fetchWorkspaceMenuTree,
    expandAllParentFolders,
    expandAllPortalParentFolders,
    fetchWorkspaceFolders,
    fetchPortalExpandedItemsInSidebar,
    setPortalExpandedItemsInList,
    setWorkspaceId,
    doNotScrollToNote,
    prevWorkspaceId,
    portalSettings.id,
    currentItemId,
  ]);
}
